import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@foundation/components/ui/card";
import { Button } from "@foundation/components/ui/button";
import { Badge } from "@foundation/components/ui/badge";
import { ScrollArea } from "@foundation/components/ui/scroll-area";
import { Stepper } from "@foundation/components/stepper";
import {
  ArrowLeft,
  ArrowRight,
  FileText,
  AlertCircle,
  Search,
  CheckCircle,
  CheckCircle2,
  Loader2,
  Filter,
} from "lucide-react";
import { useToast } from "@foundation/hooks/use-toast";
import { useUser } from "@foundation/contextapi/UserContext";
import { offlineSupabase } from "@foundation/utils/offlineSupabaseProxy";
import { enhancedClientService } from "../services/enhancedClientService";
import ScopingAILayout from "../components/RevisrLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@foundation/components/ui/tabs";
import { Label } from "@foundation/components/ui/label";
import { Alert, AlertDescription } from "@foundation/components/ui/alert";
import { Progress } from "@foundation/components/ui/progress";
import {
  compareMultipleStandardsStream,
} from "../services/quickCompareService";
import type { ComplianceFinding } from "../types/quickCompare";

interface Change {
  id: string;
  type: 'addition' | 'deletion' | 'modification';
  content: string;
  oldContent?: string;
  newContent?: string;
  lineNumber: number;
  section: string;
  status: 'pending' | 'accepted' | 'reverted';
  category: 'branding' | 'compliance' | 'formatting' | 'content';
}

interface AuditComparisonData {
  oldAudit?: {
    file: any;
    validationType: string;
    dataType: string;
  };
  newAudit?: {
    file: any;
    validationType: string;
    dataType: string;
  };
  storeId?: string | null;
  policyFileName?: string;
  standardFileName?: string;
  conversionSource?: string;
}

// Helper functions to transform ComplianceFinding to Change format
function extractSectionFromReference(reference: string): string {
  if (!reference || reference.trim() === '') {
    return 'General';
  }

  // Try to extract section number or name
  const sectionMatch = reference.match(/(?:Section|Clause|Chapter)\s*(\d+(?:\.\d+)?)/i);
  if (sectionMatch) {
    return `Section ${sectionMatch[1]}`;
  }

  // Try to extract ISO standard reference
  const isoMatch = reference.match(/(ISO\s*\d+[:\s]\d+)/i);
  if (isoMatch) {
    return isoMatch[1];
  }

  // Try to extract page reference
  const pageMatch = reference.match(/page\s*(\d+)/i);
  if (pageMatch) {
    return `Page ${pageMatch[1]}`;
  }

  // Fallback: return first 50 characters of reference
  return reference.length > 50 ? reference.substring(0, 50) + '...' : reference;
}

function mapComplianceStatusToCategory(
  status: 'non-compliant' | 'missing' | 'needs-clarification'
): 'branding' | 'compliance' | 'formatting' | 'content' {
  // All compliance findings map to 'compliance' category
  return 'compliance';
}

function determineChangeType(
  originalText: string,
  revisedText: string
): 'addition' | 'deletion' | 'modification' {
  const hasOriginal = originalText && originalText.trim() !== '';
  const hasRevised = revisedText && revisedText.trim() !== '';

  if (!hasOriginal && hasRevised) {
    return 'addition';
  }
  if (hasOriginal && !hasRevised) {
    return 'deletion';
  }
  return 'modification';
}

function generateChangeId(index: number): string {
  return `change-${index}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

function complianceFindingToChange(
  finding: ComplianceFinding,
  index: number
): Change {
  const changeType = determineChangeType(finding.originalText, finding.revisedText);
  const section = extractSectionFromReference(finding.reference);
  const category = mapComplianceStatusToCategory(finding.complianceStatus);

  return {
    id: generateChangeId(index),
    type: changeType,
    content: finding.explanation || finding.revisedText || finding.originalText || '',
    oldContent: finding.originalText || undefined,
    newContent: finding.revisedText || undefined,
    lineNumber: index + 1,
    section: section,
    status: 'pending',
    category: category,
  };
}

function transformFindingsToChanges(
  findings: ComplianceFinding[]
): Change[] {
  return findings.map((finding, index) => complianceFindingToChange(finding, index));
}

// System prompt for compliance audit (hidden from UI)
const defaultSystemPrompt = `You are a professional Policy Auditor and Compliance Specialist. Your task is to assess whether a POLICY document complies with the requirements specified in a STANDARD document.

CRITICAL: DOCUMENT IDENTIFICATION
- Documents labeled [POLICY DOCUMENT] are the policy documents being audited
- Documents labeled [STANDARD DOCUMENT] are the standards/requirements against which the policy is being evaluated
- You MUST compare the POLICY against the STANDARD - NOT the standard against itself
- The score should reflect how well the POLICY meets the STANDARD's requirements

ROLE AND OBJECTIVE:
- Act as a policy auditor reviewing the POLICY document against the STANDARD document
- Identify ONLY major and minor compliance gaps, missing requirements, and areas that need revision in the POLICY
- Focus on substantive issues that add real value - exclude minor rewording or stylistic changes
- Provide specific, actionable recommendations for POLICY changes to meet STANDARD requirements
- Calculate a comprehensive compliance score showing how well the POLICY complies with the STANDARD

CRITICAL FILTERING RULES:
1. ONLY include findings that represent MAJOR or MINOR compliance issues
2. EXCLUDE findings that are:
   - Just rewording of existing text without adding substantive value
   - Minor stylistic or grammatical changes
   - Cosmetic improvements that don't address compliance gaps
   - Text that already substantially meets the requirement (even if wording differs slightly)
3. INCLUDE findings that:
   - Represent missing critical requirements
   - Show non-compliance with specific standard requirements
   - Require substantive changes to meet compliance
   - Address gaps that could lead to compliance failures

OUTPUT FORMAT - CRITICAL:
You MUST respond with a valid JSON object (not an array). The response must include findings, scoring, and a final score.

JSON Structure:
{
  "findings": [
    {
      "originalText": "exact text from policy document that needs to be changed (or empty string if text doesn't exist)",
      "revisedText": "suggested revised or new text that complies with the standard",
      "reference": "specific reference to the standard requirement (section, clause, or page number)",
      "complianceStatus": "non-compliant" | "missing" | "needs-clarification",
      "severity": "critical" | "high" | "medium" | "low",
      "findingType": "major" | "minor",
      "explanation": "brief explanation of why this change is needed"
    }
  ],
  "score": {
    "totalRequirements": number,
    "compliantRequirements": number,
    "nonCompliantRequirements": number,
    "missingRequirements": number,
    "needsClarification": number,
    "scoreBreakdown": {
      "critical": number,
      "high": number,
      "medium": number,
      "low": number
    },
    "finalScore": number,
    "scoreExplanation": "Brief explanation of how the score was calculated"
  },
  "hasFindings": boolean,
  "message": "Summary message - either 'No findings. Policy is fully compliant.' or a brief summary of key issues"
}

SCORING METHODOLOGY:
1. Start with a base score of 100
2. Deduct points based on findings:
   - Critical finding: -10 points each
   - High severity finding: -5 points each
   - Medium severity finding: -2 points each
   - Low severity finding: -1 point each
3. Calculate: finalScore = max(0, 100 - (critical * 10 + high * 5 + medium * 2 + low * 1))
4. If no findings exist, finalScore = 100

RULES FOR OUTPUT:
1. If findings exist:
   - Populate the "findings" array with ONLY major and minor compliance issues (exclude rewording without value)
   - Each finding MUST include "findingType": "major" or "minor"
   - Major findings: Critical compliance gaps, missing essential requirements, non-compliance with core standards
   - Minor findings: Less critical issues, partial compliance, areas needing clarification
   - Calculate the score based on the methodology above
   - Set "hasFindings": true
   - Set "message" to a brief summary of key issues

2. If NO findings exist (policy is fully compliant OR only minor rewording needed):
   - Set "findings" to an empty array: []
   - Set "score" with all requirements marked as compliant
   - Set "finalScore": 100
   - Set "hasFindings": false
   - Set "message": "No findings. Policy is fully compliant with the standard."

3. Always include:
   - Complete scoring breakdown
   - Final score (0-100)
   - Clear message indicating compliance status
   - "findingType" field for each finding (CRITICAL - must be "major" or "minor")

4. Order findings by:
   - First: findingType (major first, then minor)
   - Then: severity (critical first, then high, medium, low)

5. Be specific and precise - quote exact text from documents when possible

6. Ensure all revised text is clear, actionable, and directly addresses the standard requirement

7. DO NOT include findings that are:
   - Just rewording without substantive compliance value
   - Minor stylistic improvements
   - Text that already substantially meets requirements

IMPORTANT:
- Return ONLY valid JSON - no markdown, no explanations outside the JSON structure
- Use the retrieved document chunks to find exact text and references
- Be thorough but focused on actionable compliance gaps (major and minor only)
- Ensure revised text is professional and suitable for policy documents
- Always calculate and include the score, even if there are no findings
- CRITICAL: Every finding MUST have "findingType": "major" or "minor" - this is mandatory`;

export default function ScanAndReview() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useUser();

  const [auditData, setAuditData] = useState<AuditComparisonData | null>(null);
  const [changes, setChanges] = useState<Change[]>([]);
  const [documentContent, setDocumentContent] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [isScanning, setIsScanning] = useState(false);
  const [scanComplete, setScanComplete] = useState(false);
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [orgId, setOrgId] = useState<string | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [workflowType, setWorkflowType] = useState<string>("");
  const [auditResults, setAuditResults] = useState<{
    findings?: Array<ComplianceFinding & {
      analysis?: string;
      gaps?: string[];
      recommendations?: string[];
    }>;
    score?: {
      totalRequirements?: number;
      compliantRequirements?: number;
      nonCompliantRequirements?: number;
      missingRequirements?: number;
      needsClarification?: number;
      scoreBreakdown?: {
        critical?: number;
        high?: number;
        medium?: number;
        low?: number;
      };
      finalScore?: number;
      scoreExplanation?: string;
    };
    hasFindings?: boolean;
    message?: string;
  } | null>(null);

  // Streaming state for live progress (same as QuickCompare.tsx)
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingProgress, setStreamingProgress] = useState<{ step: string; message: string }>({ step: '', message: '' });
  const [interimFindings, setInterimFindings] = useState<any[]>([]);
  const interimFindingsScrollRef = useRef<HTMLDivElement>(null);

  // Helper functions for badge colors (matching QuickCompare.tsx)
  const getSeverityColor = (severity?: string): string => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20';
      case 'high':
        return 'bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-500/20';
      case 'medium':
        return 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/20';
      case 'low':
        return 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusColor = (status?: string): string => {
    switch (status?.toLowerCase()) {
      case 'non-compliant':
        return 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20';
      case 'missing':
        return 'bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-500/20';
      case 'needs-clarification':
        return 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/20';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getFindingTypeColor = (type?: string): string => {
    switch (type?.toLowerCase()) {
      case 'major':
        return 'bg-red-600/20 text-red-800 dark:text-red-300 border-red-600/30 font-semibold';
      case 'minor':
        return 'bg-blue-600/20 text-blue-800 dark:text-blue-300 border-blue-600/30 font-semibold';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  // Get workflow type on mount
  useEffect(() => {
    const workflow = sessionStorage.getItem('workflow_type');
    setWorkflowType(workflow || '');
  }, []);

  // Update current_step to 2 when page loads (for resume workflow)
  useEffect(() => {
    const updateStepOnLoad = async () => {
      const policyReviewId = sessionStorage.getItem('policy_review_id');
      const sessionOrgId = sessionStorage.getItem('orgId');

      if (policyReviewId && user?.id) {
        try {
          // Update step to 2 when entering this page
          await fetch(`/api/revisr/policy-review/${policyReviewId}/step`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'x-user-id': user.id,
              'x-org-id': sessionOrgId || orgId || '',
            },
            body: JSON.stringify({ current_step: 2 }),
          });
          console.log('✅ Updated current_step to 2 on page load');
        } catch (err) {
          console.warn('⚠️ Could not update step on page load:', err);
        }
      }
    };

    updateStepOnLoad();
  }, [user?.id, orgId]);

  // Get orgId using the same approach as QuickCompare
  useEffect(() => {
    const getOrgId = async () => {
      if (!user?.id) return;

      try {
        const { data, error } = await offlineSupabase
          .rpc("get_user_organizations", { p_user_id: user.id });

        if (error) {
          console.warn('Could not get organizations via RPC:', error);
          try {
            const orgIds = await enhancedClientService.getUserOrganizations();
            if (orgIds && orgIds.length > 0) {
              setOrgId(orgIds[0]);
              return;
            }
          } catch (fallbackErr) {
            console.warn('Fallback method also failed:', fallbackErr);
          }
          return;
        }

        if (data && data.length > 0) {
          setOrgId(data[0].id);
        } else {
          try {
            const orgIds = await enhancedClientService.getUserOrganizations();
            if (orgIds && orgIds.length > 0) {
              setOrgId(orgIds[0]);
            }
          } catch (fallbackErr) {
            console.warn('Fallback method also failed:', fallbackErr);
          }
        }
      } catch (err) {
        console.warn('Could not get orgId:', err);
        try {
          const orgIds = await enhancedClientService.getUserOrganizations();
          if (orgIds && orgIds.length > 0) {
            setOrgId(orgIds[0]);
          }
        } catch (fallbackErr) {
          console.warn('All methods to get orgId failed:', fallbackErr);
        }
      }
    };

    getOrgId();
  }, [user?.id]);

  // Auto-scroll to new findings when they arrive
  useEffect(() => {
    if (interimFindingsScrollRef.current && interimFindings.length > 0) {
      interimFindingsScrollRef.current.scrollTop = interimFindingsScrollRef.current.scrollHeight;
    }
  }, [interimFindings.length]);

  useEffect(() => {
    const loadData = async () => {
      // Check if we're resuming a review with saved changes
      const storedChanges = sessionStorage.getItem('detectedChanges');
      const storedContent = sessionStorage.getItem('documentContent');
      const storedAuditResults = sessionStorage.getItem('auditResults');
      const storedData = sessionStorage.getItem('auditComparisonData');
      const policyReviewId = sessionStorage.getItem('policy_review_id');

      // If we have saved changes (resume workflow), load them and show completed scan
      if (storedChanges) {
        try {
          const parsedChanges = JSON.parse(storedChanges);
          if (parsedChanges && parsedChanges.length > 0) {
            console.log('📋 Resuming with saved changes:', parsedChanges.length);
            setChanges(parsedChanges);

            if (storedContent) {
              setDocumentContent(storedContent);
            }

            // Load audit results if available
            if (storedAuditResults) {
              const parsedAuditResults = JSON.parse(storedAuditResults);
              setAuditResults(parsedAuditResults);
            } else {
              // If no audit results stored, construct a minimal one from changes for display
              // This ensures the UI can render properly even without full audit results
              const constructedAuditResults = {
                findings: parsedChanges.map((change: Change, idx: number) => ({
                  originalText: change.oldContent || '',
                  revisedText: change.newContent || change.content,
                  reference: change.section,
                  complianceStatus: 'non-compliant' as const,
                  severity: 'medium' as const,
                  findingType: 'minor' as const,
                  explanation: change.content,
                })),
                score: {
                  totalRequirements: parsedChanges.length,
                  compliantRequirements: 0,
                  nonCompliantRequirements: parsedChanges.length,
                  missingRequirements: 0,
                  needsClarification: 0,
                  scoreBreakdown: {
                    critical: 0,
                    high: 0,
                    medium: parsedChanges.length,
                    low: 0,
                  },
                  finalScore: Math.max(0, 100 - (parsedChanges.length * 2)),
                  scoreExplanation: `Resumed review with ${parsedChanges.length} previously detected issues.`,
                },
                hasFindings: parsedChanges.length > 0,
                message: `Resumed review with ${parsedChanges.length} compliance issue(s) to address.`,
              };
              setAuditResults(constructedAuditResults);
              console.log('📋 Constructed audit results from stored changes');
            }

            // Load audit data for file info
            if (storedData) {
              setAuditData(JSON.parse(storedData));
            }

            setIsLoading(false);
            setScanComplete(true);
            setIsScanning(false);

            toast({
              title: "Review Resumed",
              description: `Loaded ${parsedChanges.length} previously detected changes.`,
            });
            return; // Exit early, we've loaded resume data
          }
        } catch (parseErr) {
          console.warn('Could not parse stored changes:', parseErr);
        }
      }

      // Normal flow - load audit data from session storage
      if (storedData) {
        const data = JSON.parse(storedData);
        setAuditData(data);

        if (storedContent) {
          setDocumentContent(storedContent);
        } else {
          console.warn('⚠️ No document content found in session storage');
        }

        setIsLoading(false);

        // Log stored data for debugging
        console.log('📋 Loaded audit data:', {
          storeId: data.storeId,
          policyFileName: data.policyFileName,
          standardFileName: data.standardFileName,
          conversionSource: data.conversionSource,
          oldAuditFile: data.oldAudit?.file?.name,
          newAuditFile: data.newAudit?.file?.name,
        });
      } else if (policyReviewId && user?.id) {
        // Fallback: No local data but have policy_review_id - fetch from API
        console.log('📋 No local data, fetching from API for review:', policyReviewId);
        try {
          const sessionOrgId = sessionStorage.getItem('orgId') || '';
          const response = await fetch(`/api/revisr/policy-review/${policyReviewId}`, {
            headers: {
              'x-user-id': user.id,
              'x-org-id': sessionOrgId,
            },
          });

          if (response.ok) {
            const reviewData = await response.json();
            console.log('📋 Fetched review data from API:', reviewData);

            // Store orgId if not already set
            if (!sessionOrgId && reviewData.org_id) {
              sessionStorage.setItem('orgId', reviewData.org_id);
            }

            // Build auditComparisonData from API response
            const auditComparisonData = {
              storeId: reviewData.policy_store_id || null,
              policyFileName: reviewData.policy_name || '',
              standardFileName: '', // Will be fetched separately if needed
              conversionSource: 'resume-api',
            };

            // Try to fetch standard file name from knowledge_docs if standard_store_id exists
            if (reviewData.standard_store_id && reviewData.org_id) {
              try {
                const filesResponse = await fetch(`/api/revisr/quick-compare/store/${encodeURIComponent(reviewData.standard_store_id)}/files?ragService=langgraph&category=standard`, {
                  headers: {
                    'x-user-id': user.id,
                    'x-org-id': reviewData.org_id,
                  },
                });
                if (filesResponse.ok) {
                  const { files } = await filesResponse.json();
                  if (files && files.length > 0) {
                    auditComparisonData.standardFileName = files[0].name;
                    console.log('📋 Found standard file name:', files[0].name);
                  }
                }
              } catch (filesErr) {
                console.warn('Could not fetch standard file name:', filesErr);
              }
            }

            // Store in sessionStorage for future use
            sessionStorage.setItem('auditComparisonData', JSON.stringify(auditComparisonData));
            setAuditData(auditComparisonData);

            // Try to fetch document content if policy_markdown_url exists
            if (reviewData.policy_markdown_url) {
              try {
                const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
                const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
                const contentResponse = await fetch(`${supabaseUrl}/storage/v1/object/public/knowledge_docs/${reviewData.policy_markdown_url}`, {
                  headers: {
                    'apikey': supabaseKey,
                    'Authorization': `Bearer ${supabaseKey}`,
                  },
                });
                if (contentResponse.ok) {
                  const markdownContent = await contentResponse.text();
                  sessionStorage.setItem('documentContent', markdownContent);
                  setDocumentContent(markdownContent);
                }
              } catch (contentErr) {
                console.warn('Could not fetch document content:', contentErr);
              }
            }

            // Try to fetch changes if json_changes_url exists
            if (reviewData.json_changes_url) {
              try {
                const changesResponse = await fetch(`/api/revisr/policy-review/${policyReviewId}/changes`, {
                  headers: {
                    'x-user-id': user.id,
                    'x-org-id': reviewData.org_id || sessionOrgId,
                  },
                });
                if (changesResponse.ok) {
                  const { changes, audit_results } = await changesResponse.json();
                  if (changes && changes.length > 0) {
                    sessionStorage.setItem('detectedChanges', JSON.stringify(changes));
                    setChanges(changes);
                    setScanComplete(true);

                    if (audit_results) {
                      sessionStorage.setItem('auditResults', JSON.stringify(audit_results));
                      setAuditResults(audit_results);
                    }

                    toast({
                      title: "Review Resumed",
                      description: `Loaded ${changes.length} previously detected changes.`,
                    });
                  }
                }
              } catch (changesErr) {
                console.warn('Could not fetch saved changes:', changesErr);
              }
            }

            setIsLoading(false);
            return;
          } else {
            console.warn('API returned non-ok status:', response.status);
          }
        } catch (apiErr) {
          console.warn('Could not fetch policy review from API:', apiErr);
        }

        // If API fetch failed, show error and navigate
        toast({
          title: "No audit data found",
          description: "Please start from the Review/Create Document page.",
          variant: "destructive",
        });
        navigate("/revisr/new-audit");
      } else {
        toast({
          title: "No audit data found",
          description: "Please start from the Review/Create Document page.",
          variant: "destructive",
        });
        navigate("/revisr/new-audit");
      }
    };

    loadData();
  }, [navigate, toast, user?.id]);

  const handleStartComplianceAudit = async () => {
    if (!auditData) {
      toast({
        title: "Missing Data",
        description: "Please start from the Document Upload page.",
        variant: "destructive",
      });
      return;
    }

    // Get file names from auditData (stored by NewAudit.tsx after upload)
    const policyFileName = auditData.policyFileName || auditData.oldAudit.file?.name;
    const standardFileName = auditData.standardFileName || auditData.newAudit.file?.name;

    if (!policyFileName || !standardFileName) {
      toast({
        title: "Missing File Names",
        description: "File names not found. Please go back and upload files again.",
        variant: "destructive",
      });
      return;
    }

    if (!orgId) {
      toast({
        title: "Organization Required",
        description: "Unable to determine your organization. Please refresh the page.",
        variant: "destructive",
      });
      return;
    }

    setIsScanning(true);
    setIsLoading(false); // Show the live progress UI instead of loading spinner
    setErrorMessage(null); // Clear any previous errors

    // Initialize streaming state for live progress
    setIsStreaming(true);
    setInterimFindings([]);
    setStreamingProgress({ step: 'init', message: 'Initializing compliance audit...' });

    try {
      // Build enhanced system prompt with file context
      const enhancedSystemPrompt = [
        'IMPORTANT CONTEXT:',
        `Policy Document: ${policyFileName}`,
        `Standard Document: ${standardFileName}`,
        '',
        'When analyzing compliance, compare the POLICY document against the STANDARD document.',
        'The policy document is what needs to be audited for compliance.',
        'The standard document contains the requirements that the policy must comply with.',
        '',
        defaultSystemPrompt,
      ].join('\n');

      // Run streaming comparison (files already uploaded in NewAudit.tsx)
      // Note: The system prompt (defaultSystemPrompt) is hidden from UI but is sent to the backend
      // via enhancedSystemPrompt parameter. The backend uses this prompt to guide the AI comparison.
      let finalReport = '';
      const findings: ComplianceFinding[] = [];
      let parsedAuditData: {
        findings?: Array<ComplianceFinding & {
          analysis?: string;
          gaps?: string[];
          recommendations?: string[];
        }>;
        score?: {
          totalRequirements?: number;
          compliantRequirements?: number;
          nonCompliantRequirements?: number;
          missingRequirements?: number;
          needsClarification?: number;
          scoreBreakdown?: {
            critical?: number;
            high?: number;
            medium?: number;
            low?: number;
          };
          finalScore?: number;
          scoreExplanation?: string;
        };
        hasFindings?: boolean;
        message?: string;
      } | null = null;
      let hasError = false;
      let errorMsg = '';

      await compareMultipleStandardsStream(
        policyFileName,
        [standardFileName],
        (event) => {
          if (event.type === 'step_start') {
            // Update streaming progress for live UI
            setStreamingProgress({ step: event.step, message: event.message });
            console.log('Comparison step:', event.step, event.message);
          } else if (event.type === 'interim_finding' || event.type === 'holistic_finding') {
            // Append finding immediately to show live progress
            console.log('📊 Received finding:', event.finding);
            setInterimFindings(prev => {
              const findingId = `${event.finding.sectionId || event.finding.statementId || 'unknown'}-${event.finding.standardId}-${event.finding.reference || 'no-ref'}`;
              const exists = prev.some(f => {
                const existingId = `${f.sectionId || f.statementId || 'unknown'}-${f.standardId}-${f.reference || 'no-ref'}`;
                return existingId === findingId;
              });
              if (exists) return prev;
              return [...prev, event.finding];
            });
          } else if (event.type === 'final_report') {
            finalReport = event.report;

            // Parse JSON from final report
            try {
              // Extract JSON from markdown code blocks if present
              let jsonText = finalReport.trim();
              const codeBlockRegex = /```(?:json)?\s*([\s\S]*?)\s*```/g;
              const codeBlockMatches = [...jsonText.matchAll(codeBlockRegex)];
              if (codeBlockMatches.length > 0) {
                jsonText = codeBlockMatches[codeBlockMatches.length - 1][1].trim();
              }

              // Find JSON object
              const jsonObjectMatch = jsonText.match(/\{[\s\S]*\}/);
              if (jsonObjectMatch) {
                parsedAuditData = JSON.parse(jsonObjectMatch[0]) as typeof parsedAuditData;
                if (parsedAuditData && parsedAuditData.findings && Array.isArray(parsedAuditData.findings)) {
                  findings.push(...parsedAuditData.findings);
                  // Store the full parsed data including score
                  setAuditResults(parsedAuditData);
                }
              }
            } catch (parseError) {
              console.error('Error parsing comparison results:', parseError);
              hasError = true;
              errorMsg = "Could not parse comparison results. Please try again.";
            }
          } else if (event.type === 'error') {
            console.error('Streaming error:', event.message);
            hasError = true;
            errorMsg = event.message || 'An error occurred during the comparison.';
          }
        },
        enhancedSystemPrompt, // This prompt is sent to backend but hidden from UI
        user?.id,
        orgId
      );

      // If there was an error, show it and don't proceed
      if (hasError) {
        setErrorMessage(errorMsg);
        setChanges([]);
        setAuditResults(null);
        setIsLoading(false);
        setIsScanning(false);
        setIsStreaming(false);
        setScanComplete(true);
        toast({
          title: "Comparison Error",
          description: errorMsg,
          variant: "destructive",
        });
        return;
      }

      // Transform findings to changes
      const transformedChanges = transformFindingsToChanges(findings);

      setChanges(transformedChanges);
      setIsLoading(false);
      setIsScanning(false);
      setIsStreaming(false);
      setScanComplete(true);
      setErrorMessage(null); // Clear any errors on success

      // Store changes in session storage for the Edit & Approve page (fallback)
      sessionStorage.setItem('detectedChanges', JSON.stringify(transformedChanges));
      sessionStorage.setItem('auditResults', JSON.stringify(parsedAuditData));

      // NEW: Save JSON changes to backend storage for resume workflow
      const policyReviewId = sessionStorage.getItem('policy_review_id');
      if (policyReviewId && user?.id) {
        try {
          // Save JSON changes to storage
          await fetch(`/api/revisr/policy-review/${policyReviewId}/changes`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-user-id': user.id,
              'x-org-id': orgId || '',
            },
            body: JSON.stringify({
              changes: transformedChanges,
              audit_results: parsedAuditData,
            }),
          });

          // Update step to 2
          await fetch(`/api/revisr/policy-review/${policyReviewId}/step`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'x-user-id': user.id,
              'x-org-id': orgId || '',
            },
            body: JSON.stringify({
              current_step: 2,
              audit_results: parsedAuditData,
            }),
          });

          console.log('✅ Saved changes to backend storage');
        } catch (saveError) {
          console.warn('⚠️ Could not save changes to backend:', saveError);
          // Continue even if this fails - session storage has the data
        }
      }

      toast({
        title: "Compliance Audit Complete",
        description: transformedChanges.length > 0
          ? `${transformedChanges.length} compliance issue${transformedChanges.length === 1 ? '' : 's'} identified.`
          : "No compliance issues found. Policy is fully compliant.",
      });
    } catch (error) {
      console.error('Error running compliance audit:', error);
      const errorMsg = error instanceof Error ? error.message : "Failed to run compliance audit. Please try again.";
      setErrorMessage(errorMsg);
      setChanges([]);
      setIsLoading(false);
      setIsScanning(false);
      setIsStreaming(false);
      setScanComplete(true);
      toast({
        title: "Audit Failed",
        description: errorMsg,
        variant: "destructive",
      });
    }
  };

  const handleScanDocument = () => {
    handleStartComplianceAudit();
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'branding':
        return 'bg-purple-100 text-purple-800';
      case 'compliance':
        return 'bg-blue-100 text-blue-800';
      case 'formatting':
        return 'bg-amber-100 text-amber-800';
      case 'content':
        return 'bg-emerald-100 text-emerald-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredChanges = activeFilter === 'all'
    ? changes
    : changes.filter(change => change.category === activeFilter);

  const handleNext = () => {
    // If no issues were found, skip to Preview & Export
    if (changes.length === 0 && scanComplete) {
      // Store the document content for the Preview & Export page
      sessionStorage.setItem('finalDocumentContent', documentContent);
      // Navigate with the policy ID
      const policyReviewId = sessionStorage.getItem('policy_review_id');
      if (policyReviewId) {
        navigate(`/revisr/policies/generated?id=${policyReviewId}`);
      } else {
        navigate("/revisr/preview-export");
      }
    } else {
      // Store the document content for the Edit & Approve page
      sessionStorage.setItem('documentContent', documentContent);
      navigate("/revisr/edit-approve");
    }
  };

  if (isLoading) {
    return (
      <ScopingAILayout>
        <div className="min-h-screen bg-[#FCFBF4] flex items-center justify-center overflow-x-hidden">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading document...</p>
          </div>
        </div>
      </ScopingAILayout>
    );
  }

  return (
    <ScopingAILayout>
      <div className="min-h-screen bg-[#FCFBF4] overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <Button
                variant="ghost"
                onClick={() => navigate("/revisr/select-policy")}
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Back</span>
              </Button>

              <div className="flex items-center space-x-4">
                {scanComplete && (
                  <Badge variant="outline" className="text-sm">
                    {changes.length} {changes.length === 1 ? 'issue' : 'issues'} detected
                  </Badge>
                )}
              </div>
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Scan & Review</h1>
            <p className="text-gray-600 mt-2">
              AI scans documents and identifies compliance gaps for review
            </p>
          </div>

          {/* Audit Progress Stepper */}
          {workflowType && (
            <div className="mb-8">
              <Stepper
                currentStep={workflowType === 'create_policy' ? 2 : 2}
                steps={
                  workflowType === 'create_policy'
                    ? [
                      { label: "Upload Document", description: "Upload your document for analysis" },
                      { label: "Select Template", description: "Choose formatting template" },
                      { label: "Preview & Export", description: "Finalize and export" },
                    ]
                    : [
                      { label: "Upload Documents", description: "Upload policy and standard documents" },
                      { label: "Scan & Review", description: "AI scans and identifies compliance gaps" },
                      { label: "Edit & Approve", description: "Review and approve changes" },
                      { label: "Preview & Export", description: "Finalize and export" },
                    ]
                }
              />
            </div>
          )}

          {/* Scan Button */}
          {!scanComplete && (
            <div className="flex justify-center mb-8">
              <Button
                onClick={handleScanDocument}
                disabled={isScanning}
                className="bg-primary text-primary-foreground hover:bg-primary/90 gap-2 px-8 py-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all duration-200"
                size="lg"
              >
                {isScanning ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Scanning Document...
                  </>
                ) : (
                  <>
                    <Search className="h-5 w-5" />
                    Start Compliance Audit
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Live Progress Section - Exact match to QuickCompare.tsx */}
          {isStreaming && (
            <Card className="mb-6 border-blue-500/20 bg-blue-500/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                  Live Analysis In Progress
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-blue-600 dark:text-blue-400">{streamingProgress.message}</span>
                  </div>
                  <Progress value={interimFindings.length > 0 ? (interimFindings.length % 20) * 5 : 5} className="h-2" />
                </div>

                {interimFindings.length > 0 && (
                  <div className="mt-4 space-y-3">
                    <h4 className="text-sm font-semibold">Interim Findings ({interimFindings.length})</h4>
                    <div ref={interimFindingsScrollRef} className="max-h-[300px] overflow-y-auto space-y-3 pr-2">
                      {interimFindings.map((finding, idx) => {
                        // Create unique key from finding properties
                        const findingKey = `${finding.sectionId || finding.statementId || 'unknown'}-${finding.standardId}-${idx}-${finding.reference || 'no-ref'}`;
                        return (
                          <div key={findingKey} className="p-3 rounded-md bg-background border text-sm animate-in fade-in slide-in-from-top-2">
                            <div className="flex justify-between items-start mb-1">
                              <span className="font-medium truncate max-w-[70%]">{finding.policyStatement}</span>
                              <Badge variant={
                                finding.complianceStatus === 'compliant' ? 'default' :
                                  finding.complianceStatus === 'non-compliant' ? 'destructive' : 'secondary'
                              }>
                                {finding.complianceStatus}
                              </Badge>
                            </div>
                            <details className="group">
                              <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground list-none flex items-center gap-1">
                                <span>{finding.analysis?.substring(0, 100)}...</span>
                                <span className="text-blue-500 font-medium group-open:hidden">(Show More)</span>
                                <span className="text-blue-500 font-medium hidden group-open:inline">(Show Less)</span>
                              </summary>
                              <div className="mt-2 text-xs text-muted-foreground whitespace-pre-wrap space-y-2">
                                {finding.reference && (
                                  <div className="p-2 bg-muted/50 rounded border-l-2 border-blue-500">
                                    <strong>Reference:</strong> {finding.reference}
                                  </div>
                                )}

                                <div>
                                  <strong>Analysis:</strong>
                                  <div className="mt-1">{finding.analysis}</div>
                                </div>

                                {finding.gaps && finding.gaps.length > 0 && (
                                  <div>
                                    <strong>Gaps:</strong>
                                    <ul className="list-disc list-inside pl-1 mt-1 text-red-500/80">
                                      {finding.gaps.map((gap, i) => (
                                        <li key={i}>{gap}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}

                                {finding.recommendations && finding.recommendations.length > 0 && (
                                  <div>
                                    <strong>Recommendations:</strong>
                                    <ul className="list-disc list-inside pl-1 mt-1 text-green-600/80">
                                      {finding.recommendations.map((rec, i) => (
                                        <li key={i}>{rec}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </details>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Scan Results */}
          {scanComplete && (
            <>
              {errorMessage ? (
                <div className="flex flex-col items-center justify-center p-12 text-center bg-white rounded-lg shadow-md border border-red-200 mb-8">
                  <AlertCircle className="h-16 w-16 text-red-500 mb-4" />
                  <h3 className="text-xl font-medium text-gray-900 mb-2">Comparison Error</h3>
                  <p className="text-base text-red-600 max-w-md mb-6">
                    {errorMessage}
                  </p>
                  <Button
                    onClick={() => {
                      setErrorMessage(null);
                      setIsScanning(false);
                      setScanComplete(false);
                    }}
                    variant="outline"
                    className="gap-2 px-6 py-2"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Try Again
                  </Button>
                </div>
              ) : auditResults && auditResults.findings && auditResults.findings.length === 0 && !auditResults.hasFindings ? (
                <div className="space-y-6">
                  {/* Score Summary Card */}
                  {auditResults.score && (
                    <Card className="border-primary/20 bg-primary/5">
                      <CardHeader>
                        <CardTitle className="flex items-center justify-between">
                          <span>Compliance Score</span>
                          <Badge className="text-2xl px-4 py-2" variant={auditResults.score.finalScore && auditResults.score.finalScore >= 80 ? 'default' : auditResults.score.finalScore && auditResults.score.finalScore >= 60 ? 'secondary' : 'destructive'}>
                            {auditResults.score.finalScore ?? 0}/100
                          </Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {auditResults.score.scoreBreakdown && (
                          <div className="grid grid-cols-4 gap-2 text-sm">
                            <div>
                              <span className="text-muted-foreground">Critical:</span> <strong className="text-red-600">{auditResults.score.scoreBreakdown.critical ?? 0}</strong>
                            </div>
                            <div>
                              <span className="text-muted-foreground">High:</span> <strong className="text-orange-600">{auditResults.score.scoreBreakdown.high ?? 0}</strong>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Medium:</span> <strong className="text-yellow-600">{auditResults.score.scoreBreakdown.medium ?? 0}</strong>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Low:</span> <strong className="text-blue-600">{auditResults.score.scoreBreakdown.low ?? 0}</strong>
                            </div>
                          </div>
                        )}
                        {auditResults.score.scoreExplanation && (
                          <p className="text-sm text-muted-foreground">{auditResults.score.scoreExplanation}</p>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* No Findings Case */}
                  <Card className="border-green-500/20 bg-green-500/5">
                    <CardContent className="py-8 text-center">
                      <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">No Compliance Issues Found</h3>
                      <p className="text-muted-foreground">
                        {auditResults.message || 'Policy is fully compliant with the standard.'}
                      </p>
                      {auditResults.score && (
                        <div className="mt-4">
                          <Badge className="text-xl px-4 py-2" variant="default">
                            Score: {auditResults.score.finalScore ?? 100}/100
                          </Badge>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <div className="flex justify-center items-center gap-4">
                    <Button
                      onClick={handleNext}
                      className="bg-[#33b864] text-white hover:bg-[#33b864]/90 gap-2 px-8 py-3 text-lg font-medium shadow-lg hover:shadow-xl transition-all duration-200"
                    >
                      Continue to Preview & Export
                      <ArrowRight className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
              ) : auditResults && auditResults.findings && auditResults.findings.length > 0 ? (
                <div className="space-y-6">
                  {/* Score Summary Card */}
                  {auditResults.score && (
                    <Card className="border-primary/20 bg-primary/5">
                      <CardHeader>
                        <CardTitle className="flex items-center justify-between">
                          <span>Compliance Score</span>
                          <Badge className="text-2xl px-4 py-2" variant={auditResults.score.finalScore && auditResults.score.finalScore >= 80 ? 'default' : auditResults.score.finalScore && auditResults.score.finalScore >= 60 ? 'secondary' : 'destructive'}>
                            {auditResults.score.finalScore ?? 0}/100
                          </Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {auditResults.score.scoreBreakdown && (
                          <div className="grid grid-cols-4 gap-2 text-sm">
                            <div>
                              <span className="text-muted-foreground">Critical:</span> <strong className="text-red-600">{auditResults.score.scoreBreakdown.critical ?? 0}</strong>
                            </div>
                            <div>
                              <span className="text-muted-foreground">High:</span> <strong className="text-orange-600">{auditResults.score.scoreBreakdown.high ?? 0}</strong>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Medium:</span> <strong className="text-yellow-600">{auditResults.score.scoreBreakdown.medium ?? 0}</strong>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Low:</span> <strong className="text-blue-600">{auditResults.score.scoreBreakdown.low ?? 0}</strong>
                            </div>
                          </div>
                        )}
                        {auditResults.score.scoreExplanation && (
                          <p className="text-sm text-muted-foreground">{auditResults.score.scoreExplanation}</p>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Message Banner */}
                  {auditResults.message && (
                    <Alert variant={auditResults.hasFindings ? 'default' : 'default'} className={auditResults.hasFindings ? 'border-yellow-500/20 bg-yellow-500/5' : 'border-green-500/20 bg-green-500/5'}>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{auditResults.message}</AlertDescription>
                    </Alert>
                  )}

                  {/* Findings List */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-muted-foreground">
                        Found <strong>{auditResults.findings.length}</strong> compliance {auditResults.findings.length === 1 ? 'finding' : 'findings'}
                      </p>
                    </div>

                    <div className="space-y-4">
                      {auditResults.findings.map((finding, index) => (
                        <Card key={index} className="border-l-4 border-l-primary">
                          <CardHeader className="pb-3">
                            <div className="flex items-start justify-between gap-2">
                              <CardTitle className="text-lg">
                                Finding #{index + 1}
                              </CardTitle>
                              <div className="flex gap-2 flex-wrap">
                                {finding.findingType && (
                                  <Badge className={getFindingTypeColor(finding.findingType)}>
                                    {finding.findingType.toUpperCase()}
                                  </Badge>
                                )}
                                {finding.severity && (
                                  <Badge className={getSeverityColor(finding.severity)}>
                                    {finding.severity.toUpperCase()}
                                  </Badge>
                                )}
                                {finding.complianceStatus && (
                                  <Badge className={getStatusColor(finding.complianceStatus)}>
                                    {finding.complianceStatus.replace('-', ' ').toUpperCase()}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            {finding.explanation && (
                              <div>
                                <Label className="text-sm font-semibold mb-2 block">Explanation</Label>
                                <p className="text-sm text-muted-foreground">{finding.explanation}</p>
                              </div>
                            )}

                            {/* originalText - Always show if present or if finding exists */}
                            <div>
                              <Label className="text-sm font-semibold mb-2 block text-destructive">
                                Original Text {finding.originalText ? '(Non-Compliant)' : '(Missing Content)'}
                              </Label>
                              <div className="bg-destructive/10 p-3 rounded-md text-sm border border-destructive/20">
                                {finding.originalText ? `"${finding.originalText}"` : '(No existing text - content needs to be added)'}
                              </div>
                            </div>

                            {finding.revisedText && (
                              <div>
                                <Label className="text-sm font-semibold mb-2 block text-green-600 dark:text-green-400">
                                  Recommended Revision
                                </Label>
                                <div className="bg-green-500/10 p-3 rounded-md text-sm border border-green-500/20">
                                  "{finding.revisedText}"
                                </div>
                              </div>
                            )}

                            {finding.reference && (
                              <div>
                                <Label className="text-sm font-semibold mb-2 block">Reference</Label>
                                <p className="text-sm text-muted-foreground">{finding.reference}</p>
                              </div>
                            )}

                            {finding.analysis && (
                              <div>
                                <Label className="text-sm font-semibold mb-2 block">Analysis</Label>
                                <p className="text-sm text-muted-foreground">{finding.analysis}</p>
                              </div>
                            )}

                            {/* gaps - Always show */}
                            <div>
                              <Label className="text-sm font-semibold mb-2 block">Gaps</Label>
                              {finding.gaps && finding.gaps.length > 0 ? (
                                <ul className="list-disc list-inside text-sm text-red-600 dark:text-red-400 space-y-1">
                                  {finding.gaps.map((gap, i) => (
                                    <li key={i}>{gap}</li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="text-sm text-muted-foreground italic">No specific gaps identified</p>
                              )}
                            </div>

                            {/* recommendations - Always show */}
                            <div>
                              <Label className="text-sm font-semibold mb-2 block">Recommendations</Label>
                              {finding.recommendations && finding.recommendations.length > 0 ? (
                                <ul className="list-disc list-inside text-sm text-green-600 dark:text-green-400 space-y-1">
                                  {finding.recommendations.map((rec, i) => (
                                    <li key={i}>{rec}</li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="text-sm text-muted-foreground italic">No specific recommendations provided</p>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>

                  {/* Next Button */}
                  <div className="flex justify-center items-center gap-4">
                    <Button
                      variant="ghost"
                      onClick={() => navigate("/revisr/new-audit")}
                      className="flex items-center space-x-2"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      <span>Back</span>
                    </Button>

                    <Button
                      onClick={handleNext}
                      className="bg-[#33b864] text-white hover:bg-[#33b864]/90 gap-2 px-8 py-3 text-lg font-medium shadow-lg hover:shadow-xl transition-all duration-200"
                    >
                      Edit and Approve
                      <ArrowRight className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center p-12 text-center bg-white rounded-lg shadow-md border mb-8">
                  <CheckCircle className="h-16 w-16 text-green-500 mb-4" />
                  <h3 className="text-xl font-medium text-gray-900 mb-2">No Issues Found</h3>
                  <p className="text-base text-gray-500 max-w-md mb-6">
                    Your document meets all standards and requirements. You can proceed to the Finalise and Export step.
                  </p>
                  <Button
                    onClick={handleNext}
                    className="bg-primary text-primary-foreground hover:bg-primary/90 gap-2 px-6 py-2"
                  >
                    <ArrowRight className="h-4 w-4" />
                    Continue to Preview & Export
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </ScopingAILayout>
  );
}
